"use client"

import React, { useActionState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { updatePersonalAction, UpdatePersonalActionState } from "@/actions/profileActions"
import { subscribeNewsletter } from "@/actions/subscriptionAction"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { format, isValid, parse } from "date-fns"
import { de, fr, nl } from "date-fns/locale"
import { CalendarIcon, LoaderCircle, PlusIcon, SaveIcon } from "lucide-react"
import { Controller, useForm } from "react-hook-form"

import { hasEntries } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { zodResolver } from "@/lib/polyfill/zodResolver"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Form as RHFForm,
} from "@/components/ui/components"
import { Calendar } from "@/components/ui/components/calendar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/components/select"
import AccountBackLinkClient from "@/components/account/AccountBackLinkClient"
import { normalizeBirthday } from "@/components/account/profile/personal/normalizeBirthday"
import { UpdatePersonalSchema } from "@/components/account/profile/profile"
import { FormRow } from "@/components/account/shared/FormRow"
import NewsletterCheckbox from "@/components/auth/signup/NewsletterCheckbox"
import { UbAccountProfilePersonalViewStoryblok } from "@/components/storyblok/component-types"
import UbRichText from "@/components/storyblok/UbRichText"

const resolver = zodResolver(UpdatePersonalSchema)

interface AccountProfilePersonalProps {
  accountContext: AccountContext
  blok: UbAccountProfilePersonalViewStoryblok
}

export default function AccountProfilePersonalClient(props: AccountProfilePersonalProps) {
  const { setProfileUpdated } = useUbStorefrontState()
  const router = useRouter()

  function handleNewsletter(formData: FormData) {
    if (formData.get("newsletter")) {
      subscribeNewsletter().then((values) => {
        props.accountContext.userData && (props.accountContext.userData.subscriptionPending = true)
      })
    }
  }

  async function action(initialState: UpdatePersonalActionState, formData: FormData) {
    return updatePersonalAction(
      initialState,
      formData,
      props.accountContext.userData,
      props.accountContext.customerData
    ).then(async (state) => {
      if (!hasEntries(state.errors)) {
        handleNewsletter(formData)
        setProfileUpdated(true)
        router.push(getLinkWithPrefixFromURLAndLocale("/account/profile", props.accountContext.sbLocale))
      }
      return {
        values: state.values,
        errors: state.errors,
      }
    })
  }

  const [state, formAction, isPending] = useActionState(action, {
    values: {
      title: props.accountContext.userData?.title ?? "",
      honorific: props.accountContext.userData?.honorific ?? "",
      firstName: props.accountContext.userData?.firstName ?? "",
      lastName: props.accountContext.userData?.lastName ?? "",
      department: props.accountContext.userData?.department ?? "",
      phoneBusiness: props.accountContext.userData?.phoneBusiness ?? "",
      fax: props.accountContext.userData?.fax ?? "",
      birthday: normalizeBirthday(props.accountContext.userData?.birthday),
      preferredLanguage: props.accountContext.userData?.preferredLanguage ?? props.accountContext.preferredLanguages[0],
    },
    errors: {},
  })

  const form = useForm({
    values: state.values,
    errors: state.errors,
    resetOptions: {
      keepErrors: true,
    },
    mode: "onChange",
    resolver,
  })

  const { formState, register, reset, control } = form

  useEffect(() => {
    console.log("reset", state.values)
    const normalizedValues = {
      ...state.values,
      birthday: normalizeBirthday((state.values as any).birthday as string),
    }
    reset(normalizedValues, {
      keepErrors: true,
    })
  }, [reset, state.values])

  const localeCalender =
    state.values.preferredLanguage == "fr_FR" ? fr : state.values.preferredLanguage == "nl_NL" ? nl : de

  return (
    <>
      <AccountBackLinkClient label={props.blok.label_common_back} />
      <div className={"relative flex w-full flex-col gap-2 lg:gap-6"}>
        <div className={"flex flex-col gap-2"}>
          <h2 className={"text-lg font-bold"}>{props.blok.title}</h2>
          <p>{props.blok.description}</p>
          <p className={"text-xs"}>{props.blok.required}</p>
        </div>

        <div>
          <RHFForm {...form}>
            <form action={formAction}>
              <div className="flex flex-col gap-2 lg:gap-6">
                <FormRow label={props.blok.salutation} htmlFor={"title"} required>
                  <Controller
                    control={control}
                    name="title"
                    render={({ field }) => (
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        name={field.name}
                        className={cn(
                          "flex flex-row justify-between gap-1 rounded-lg border border-gray-300 bg-white px-5 py-3",
                          { "border-primary": formState.errors.title }
                        )}
                      >
                        <div
                          className="
                            flex w-fit items-center space-x-2 text-base ring-0 outline-hidden
                            hover:border-black
                          "
                        >
                          <RadioGroupItem value={props.blok.salutation_female ?? ""} id="r1" />
                          <Label className={"font-bold"} htmlFor="r1">
                            {props.blok.salutation_female}
                          </Label>
                        </div>
                        <div
                          className="
                            flex w-fit items-center space-x-2 text-base ring-0 outline-hidden
                            hover:border-black
                          "
                        >
                          <RadioGroupItem value={props.blok.salutation_male ?? ""} id="r2" />
                          <Label className={"font-bold"} htmlFor="r2">
                            {props.blok.salutation_male}
                          </Label>
                        </div>
                        <div
                          className="
                            flex w-fit items-center space-x-2 text-base ring-0 outline-hidden
                            hover:border-black
                          "
                        >
                          <RadioGroupItem value={props.blok.salutation_divers ?? ""} id="r3" />
                          <Label className={"font-bold"} htmlFor="r3">
                            {props.blok.salutation_divers}
                          </Label>
                        </div>
                      </RadioGroup>
                    )}
                  />
                  {formState.errors.title && (
                    <p className={"mt-1 text-xs text-red-600"} role="alert">
                      {props.blok.salutation_missing}
                    </p>
                  )}
                </FormRow>
                <FormRow label={props.blok.honorific} htmlFor={"honorific"}>
                  <Input
                    {...register("honorific")}
                    className={cn("", { "border-primary": formState.errors.honorific })}
                  />
                </FormRow>
                <FormRow label={props.blok.first_name} htmlFor={"firstName"} required>
                  <Input
                    {...register("firstName")}
                    className={cn("", { "border-primary": formState.errors.firstName })}
                  />
                  {formState.errors.firstName && (
                    <p className={"mt-1 text-xs text-red-600"} role="alert">
                      {props.blok.first_name_missing}
                    </p>
                  )}
                </FormRow>
                <FormRow label={props.blok.last_name} htmlFor={"lastName"} required>
                  <Input
                    {...register("lastName")}
                    className={cn("", { "border-primary": formState.errors.lastName })}
                  />
                  {formState.errors.lastName && (
                    <p className={"mt-1 text-xs text-red-600"} role="alert">
                      {props.blok.last_name_missing}
                    </p>
                  )}
                </FormRow>
                {props.accountContext.customerData?.customerType !== "PRIVATE" && (
                  <FormRow label={props.blok.department} htmlFor={"department"}>
                    <Input
                      {...register("department")}
                      className={cn("", { "border-primary": formState.errors.department })}
                    />
                  </FormRow>
                )}
                <FormRow label={props.blok.phone} htmlFor={"phoneBusiness"}>
                  <Input
                    {...register("phoneBusiness")}
                    className={cn("", { "border-primary": formState.errors.phoneBusiness })}
                  />
                </FormRow>
                <FormRow label={props.blok.fax} htmlFor={"fax"}>
                  <Input {...register("fax")} className={cn("", { "border-primary": formState.errors.fax })} />
                </FormRow>
                <FormRow label={props.blok.birthday} htmlFor={"birthday"}>
                  <FormField
                    control={control}
                    name="birthday"
                    render={({ field }) => {
                      const parsed = field.value ? parse(field.value as string, "dd.MM.yyyy", new Date()) : undefined
                      const selectedDate = parsed && isValid(parsed) ? parsed : undefined
                      return (
                        <FormItem className="flex flex-col">
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  size={"defaultNoGrow"}
                                  className={cn(
                                    "w-full border-gray-200 pl-3 text-left text-sm font-normal text-black",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {selectedDate ? (
                                    format(selectedDate, "PPP", { locale: localeCalender })
                                  ) : (
                                    <span>{props.blok.choose_date}</span>
                                  )}
                                  <CalendarIcon className="ml-auto size-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 text-sm" align="start">
                              <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={(date) => field.onChange(date ? format(date, "dd.MM.yyyy") : "")}
                                disabled={(date) => date > new Date()}
                                captionLayout="dropdown"
                                locale={localeCalender}
                              />
                            </PopoverContent>
                          </Popover>
                          <input type="hidden" name={field.name} value={field.value ?? ""} />
                        </FormItem>
                      )
                    }}
                  />

                  {formState.errors.birthday && (
                    <p className={"mt-1 text-xs text-red-600"} role="alert">
                      {props.blok.birthday_invalid}
                    </p>
                  )}
                </FormRow>
                <FormRow label={props.blok.preferred_language} htmlFor={"preferredLanguage"} required>
                  <FormField
                    control={control}
                    name="preferredLanguage"
                    render={({ field }) => (
                      <>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger
                            id="preferredLanguage"
                            className={"w-full rounded-lg border-gray-300 pl-3 text-left text-sm font-normal"}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {props.accountContext.preferredLanguages.map((language) => (
                              <SelectItem key={language} value={language}>
                                {props.blok["language_" + language]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* Ensure the value is submitted with native form post */}
                        <input type="hidden" name={field.name} value={field.value ?? ""} />
                      </>
                    )}
                  />
                  {formState.errors.preferredLanguage && (
                    <p className={"mt-1 text-xs text-red-600"} role="alert">
                      {props.blok.preferred_language_missing}
                    </p>
                  )}
                </FormRow>
                <FormRow label={""} htmlFor={""}>
                  {props.accountContext.userData?.subscriptionPending ? (
                    <Label>{props.blok.newsletter_pending}</Label>
                  ) : (
                    <NewsletterCheckbox
                      defaultChecked={props.accountContext.subscriptionActive}
                      disabled={props.accountContext.subscriptionActive}
                      text={props.blok.newsletter}
                    />
                  )}
                </FormRow>

                <div className="flex flex-wrap justify-between gap-2 lg:justify-end">
                  <Button type={"submit"} size={"defaultNoGrow"} disabled={isPending} className={"gap-1"}>
                    {!isPending ? (
                      <SaveIcon className={"inline size-5"} />
                    ) : (
                      <LoaderCircle className={"inline size-5 motion-safe:animate-spin"} />
                    )}
                    <span>{props.blok.submit}</span>
                  </Button>

                  <Button
                    type={"reset"}
                    variant="outline"
                    size={"defaultNoGrow"}
                    className={"gap-1"}
                    onClick={() => router.back()}
                  >
                    <PlusIcon className={"inline size-5 rotate-45"} />
                    <span>{props.blok.cancel}</span>
                  </Button>
                </div>
              </div>
            </form>
          </RHFForm>
        </div>
        {props.blok.help && (
          <div>
            <UbRichText blok={{ content: props.blok.help, _uid: "", component: "ub_rich_text" }} />
          </div>
        )}
      </div>
    </>
  )
}
